import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { PermissionEnum, RiskLevelEnum } from '@/api/enum/common.enum';
import { useGetStaffAllListQuery } from '@/api/common';
import { useGetMemberTreeQuery } from '@/api/member-management/intervention';
import {
	CaseWorkerStatisticsReportFilter,
	InterventionReportFilter,
	SpsFormReportFilter,
	useExportActiveFamilyMemberReportMutation,
	useExportAllMemberReportMutation,
	useExportCaseWorkerStatisticsReportMutation,
	useExportInterventionReportMutation,
	useExportOpForm18aReportMutation,
	useExportSpsFormReportMutation,
} from '@/api/report/report';
import AppDateRangePicker from '@/common/components/form-inputs/AppDateRangePicker';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import AppAutocompleteCheckbox from '@/common/components/form-inputs/AppAutocompleteCheckbox';
import AppMonthDatePicker from '@/common/components/form-inputs/AppMonthDatePicker';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { API_DATE_FORMAT, DISPLAY_DATE_MONTH_FORMAT } from '@/utils/dateHelper';

// Permissions required by each report endpoint.
export const REPORT_PERMISSIONS = {
	allMember: PermissionEnum.SummaryOfMember,
	familyMember: PermissionEnum.SummaryOfFamilyMember,
	intervention: PermissionEnum.CaseManagementCentreInterventionSummary,
	workerStatistics: PermissionEnum.CaseManagementMyInterventionSummary,
	opForm18a: PermissionEnum.CaseManagementMyInterventionSummary,
	spsForm: PermissionEnum.CaseManagementCentreInterventionSummary,
};

type ReportKey = keyof typeof REPORT_PERMISSIONS;

interface ReportDialogProps {
	title: string;
	loading: boolean;
	onClose: () => void;
	onDownload: () => void;
	children?: ReactNode;
}

function ReportDialog({
	title,
	loading,
	onClose,
	onDownload,
	children,
}: ReportDialogProps) {
	const { t } = useTranslation();
	return (
		<Dialog
			open
			onClose={loading ? undefined : onClose}
			maxWidth='sm'
			fullWidth
		>
			<DialogTitle variant='h5' sx={{ pr: 6 }}>
				{title}
			</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={onClose}
				disabled={loading}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent sx={{ pt: '8px !important' }}>
				{children ?? (
					<Typography variant='body2' color='text.secondary'>
						{t('report.noFilters')}
					</Typography>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					sx={{ textTransform: 'none' }}
					onClick={onClose}
					disabled={loading}
				>
					{t('button.cancel')}
				</Button>
				<Button
					variant='contained'
					startIcon={<FileDownloadOutlinedIcon />}
					loading={loading}
					loadingPosition='start'
					onClick={onDownload}
				>
					{t('report.downloadExcel')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

interface DialogProps {
	onClose: () => void;
}

// Close the dialog only when the download succeeded; errors are shown by the global API error modal.
const closeOnSuccess = (result: { error?: unknown }, onClose: () => void) => {
	if (!('error' in result) || !result.error) onClose();
};

function AllMemberReportDialog({ onClose }: DialogProps) {
	const { t } = useTranslation();
	const [exportReport, { isLoading }] = useExportAllMemberReportMutation();
	return (
		<ReportDialog
			title={t('report.allMemberReport')}
			loading={isLoading}
			onClose={onClose}
			onDownload={async () => closeOnSuccess(await exportReport(), onClose)}
		/>
	);
}

function FamilyMemberReportDialog({ onClose }: DialogProps) {
	const { t } = useTranslation();
	const [exportReport, { isLoading }] =
		useExportActiveFamilyMemberReportMutation();
	return (
		<ReportDialog
			title={t('report.allFamilyMemberReport')}
			loading={isLoading}
			onClose={onClose}
			onDownload={async () => closeOnSuccess(await exportReport(), onClose)}
		/>
	);
}

function useBranchStaffOptions() {
	const userInfo = useSelector(selectMyInfo);
	const { data: staffList }: any = useGetStaffAllListQuery(
		userInfo?.currentBranch?.id || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !userInfo?.currentBranch,
		}
	);
	return (staffList?.data || []) as { id: string; name: string }[];
}

function InterventionReportDialog({ onClose }: DialogProps) {
	const { t } = useTranslation();
	const [exportReport, { isLoading }] = useExportInterventionReportMutation();
	const staffOptions = useBranchStaffOptions();
	const { data: memberTreeList }: any = useGetMemberTreeQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { control, handleSubmit } = useForm<InterventionReportFilter>({
		defaultValues: {
			staffIds: [],
			dateFrom: '',
			dateTo: '',
			riskLevels: [],
			userIds: [],
		},
	});

	const riskLevelOptions = [
		{ id: RiskLevelEnum.Low, name: t('report.riskLow') },
		{ id: RiskLevelEnum.Medium, name: t('report.riskMedium') },
		{ id: RiskLevelEnum.High, name: t('report.riskHigh') },
	];

	const memberOptions = (memberTreeList?.data || []).map((item: any) => ({
		...item,
		name: item.label,
		childrenList: (item.childrenList || []).map((child: any) => ({
			...child,
			name: `${child.nameEn ?? ''} ${child.nameZh ?? ''}`.trim(),
		})),
	}));

	const onDownload = handleSubmit(async (filter) =>
		closeOnSuccess(await exportReport(filter), onClose)
	);

	return (
		<ReportDialog
			title={t('report.interventionReport')}
			loading={isLoading}
			onClose={onClose}
			onDownload={onDownload}
		>
			<Grid container rowSpacing={2} columnSpacing={1}>
				<Grid size={12}>
					<AppDateRangePicker
						control={control}
						startDateName='dateFrom'
						endDateName='dateTo'
						label={t('report.interventionDate')}
					/>
				</Grid>
				<Grid size={12}>
					<AppMultipleDropDownMenu
						control={control}
						name='riskLevels'
						label={t('case.riskLevel')}
						options={riskLevelOptions}
						emptyArrayIfNoSelection
					/>
				</Grid>
				<Grid size={12}>
					<AppMultipleDropDownMenu
						control={control}
						name='staffIds'
						label={t('case.inWorker')}
						options={staffOptions}
						emptyArrayIfNoSelection
					/>
				</Grid>
				<Grid size={12}>
					<AppAutocompleteCheckbox
						control={control}
						name='userIds'
						label={t('report.member')}
						options={memberOptions}
						isFormHelperText={false}
					/>
				</Grid>
			</Grid>
			<Typography
				variant='caption'
				color='text.secondary'
				display='block'
				marginTop={1}
			>
				{t('report.filterHint')}
			</Typography>
		</ReportDialog>
	);
}

// Reports filtered by a month and a case worker (worker statistics, OP Form 18a).
function MonthlyStaffReportDialog({
	onClose,
	canSelectStaff,
	title,
	useExportMutation,
}: DialogProps & {
	canSelectStaff: boolean;
	title: string;
	useExportMutation:
		| typeof useExportCaseWorkerStatisticsReportMutation
		| typeof useExportOpForm18aReportMutation;
}) {
	const { t } = useTranslation();
	const userInfo = useSelector(selectMyInfo);
	const [exportReport, { isLoading }] = useExportMutation();
	const staffOptions = useBranchStaffOptions();
	const ownStaffOption = staffOptions.filter(
		(staff) => staff.id === userInfo?.id
	);
	if (ownStaffOption.length === 0) {
		ownStaffOption.push({
			id: userInfo?.id ?? '',
			name: (userInfo as any)?.name ?? userInfo?.nickname ?? '',
		});
	}

	const { control, handleSubmit } = useForm<CaseWorkerStatisticsReportFilter>({
		defaultValues: {
			// default to the last completed month
			reportMonth: dayjs().subtract(1, 'month').format(API_DATE_FORMAT),
			staffId: userInfo?.id ?? '',
		},
	});

	const onDownload = handleSubmit(async ({ reportMonth, staffId }) =>
		closeOnSuccess(
			await exportReport({
				reportMonth: dayjs(reportMonth).format(DISPLAY_DATE_MONTH_FORMAT),
				staffId: canSelectStaff && staffId ? staffId : undefined,
			}),
			onClose
		)
	);

	return (
		<ReportDialog
			title={title}
			loading={isLoading}
			onClose={onClose}
			onDownload={onDownload}
		>
			<Grid container rowSpacing={2} columnSpacing={1}>
				<Grid size={12}>
					<AppMonthDatePicker
						control={control}
						name='reportMonth'
						label={t('report.reportMonth') + '*'}
						maxDate={dayjs().endOf('month')}
						rules={{ required: t('errorMessage.pleaseSelect') }}
					/>
				</Grid>
				<Grid size={12}>
					<AppDropDownMenu
						control={control}
						name='staffId'
						label={t('report.caseWorker')}
						options={canSelectStaff ? staffOptions : ownStaffOption}
						disabled={!canSelectStaff}
					/>
				</Grid>
			</Grid>
		</ReportDialog>
	);
}

// Financial year starts in April: Q1 Apr–Jun, Q2 Jul–Sep, Q3 Oct–Dec, Q4 Jan–Mar.
const getFinancialQuarter = (date: dayjs.Dayjs) => {
	const month = date.month();
	return {
		financialYear: month >= 3 ? date.year() : date.year() - 1,
		quarter: month >= 3 ? Math.floor((month - 3) / 3) + 1 : 4,
	};
};

const formatFinancialYear = (year: number) =>
	`${year}-${String((year + 1) % 100).padStart(2, '0')}`;

const FINANCIAL_YEAR_OPTION_COUNT = 10;

function SpsFormReportDialog({ onClose }: DialogProps) {
	const { t } = useTranslation();
	const [exportReport, { isLoading }] = useExportSpsFormReportMutation();
	const current = getFinancialQuarter(dayjs());
	// default to the last completed quarter
	const defaultValues: SpsFormReportFilter =
		current.quarter === 1
			? { financialYear: current.financialYear - 1, quarter: 4 }
			: { financialYear: current.financialYear, quarter: current.quarter - 1 };

	const { control, handleSubmit, watch, setValue, getValues } =
		useForm<SpsFormReportFilter>({ defaultValues });
	const financialYear = watch('financialYear');
	const maxQuarter =
		financialYear >= current.financialYear ? current.quarter : 4;

	useEffect(() => {
		if (getValues('quarter') > maxQuarter) setValue('quarter', maxQuarter);
	}, [maxQuarter, getValues, setValue]);

	const financialYearOptions = Array.from(
		{ length: FINANCIAL_YEAR_OPTION_COUNT },
		(_, index) => current.financialYear - index
	).map((year) => ({ id: year, name: formatFinancialYear(year) }));

	const quarterOptions = [1, 2, 3, 4].map((quarter) => ({
		id: quarter,
		name: t(`report.quarter${quarter}`),
		disabled: quarter > maxQuarter,
	}));

	const onDownload = handleSubmit(async (filter) =>
		closeOnSuccess(await exportReport(filter), onClose)
	);

	return (
		<ReportDialog
			title={t('report.spsFormReport')}
			loading={isLoading}
			onClose={onClose}
			onDownload={onDownload}
		>
			<Grid container rowSpacing={2} columnSpacing={1}>
				<Grid size={12}>
					<AppDropDownMenu
						control={control}
						name='financialYear'
						label={t('report.financialYear') + '*'}
						options={financialYearOptions}
						rules={{ required: t('errorMessage.pleaseSelect') }}
					/>
				</Grid>
				<Grid size={12}>
					<AppDropDownMenu
						control={control}
						name='quarter'
						label={t('report.quarter') + '*'}
						options={quarterOptions}
						rules={{ required: t('errorMessage.pleaseSelect') }}
					/>
				</Grid>
			</Grid>
			<Typography
				variant='caption'
				color='text.secondary'
				display='block'
				marginTop={1}
			>
				{t('report.quarterNotStartedHint')}
			</Typography>
		</ReportDialog>
	);
}

export default function ReportSummaryPage() {
	const { t } = useTranslation();
	const userInfo = useSelector(selectMyInfo);
	const permissions: number[] = userInfo?.permissionList ?? [];
	const has = (permission: PermissionEnum) => permissions.includes(permission);
	const [selected, setSelected] = useState<ReportKey | null>(null);
	const closeDialog = () => setSelected(null);

	const reports: {
		key: ReportKey;
		icon: ReactNode;
		title: string;
	}[] = [
		{
			key: 'allMember' as const,
			icon: <AssignmentIndOutlinedIcon />,
			title: t('report.allMemberReport'),
		},
		{
			key: 'familyMember' as const,
			icon: <Groups2OutlinedIcon />,
			title: t('report.allFamilyMemberReport'),
		},
		{
			key: 'intervention' as const,
			icon: <EventNoteOutlinedIcon />,
			title: t('report.interventionReport'),
		},
		{
			key: 'workerStatistics' as const,
			icon: <InsertChartOutlinedIcon />,
			title: t('report.workerStatisticsReport'),
		},
		{
			key: 'opForm18a' as const,
			icon: <DescriptionOutlinedIcon />,
			title: t('report.opForm18aReport'),
		},
		{
			key: 'spsForm' as const,
			icon: <FactCheckOutlinedIcon />,
			title: t('report.spsFormReport'),
		},
	].filter((report) => has(REPORT_PERMISSIONS[report.key]));

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.reportSummary')}
				</Typography>
			</Box>
			<Typography variant='body2' color='text.secondary' marginTop={1}>
				{t('report.pageDesc')}
			</Typography>

			<Card sx={{ marginTop: 3, width: '100%', maxWidth: 960 }}>
				{reports.length === 0 ? (
					<CardContent>
						<Typography variant='body2' color='text.secondary'>
							{t('report.noPermission')}
						</Typography>
					</CardContent>
				) : (
					<List disablePadding>
						{reports.map((report, index) => (
							<Fragment key={report.key}>
								{index > 0 && <Divider component='li' />}
								<ListItemButton
									onClick={() => setSelected(report.key)}
									sx={{ py: 2, px: 3 }}
								>
									<ListItemIcon sx={{ color: 'chipGreen.main', minWidth: 48 }}>
										{report.icon}
									</ListItemIcon>
									<ListItemText
										primary={report.title}
										slotProps={{ primary: { variant: 'body2' } }}
									/>
									<ChevronRightIcon color='action' />
								</ListItemButton>
							</Fragment>
						))}
					</List>
				)}
			</Card>

			{selected === 'allMember' && (
				<AllMemberReportDialog onClose={closeDialog} />
			)}
			{selected === 'familyMember' && (
				<FamilyMemberReportDialog onClose={closeDialog} />
			)}
			{selected === 'intervention' && (
				<InterventionReportDialog onClose={closeDialog} />
			)}
			{selected === 'workerStatistics' && (
				<MonthlyStaffReportDialog
					onClose={closeDialog}
					canSelectStaff={has(REPORT_PERMISSIONS.intervention)}
					title={t('report.workerStatisticsReport')}
					useExportMutation={useExportCaseWorkerStatisticsReportMutation}
				/>
			)}
			{selected === 'opForm18a' && (
				<MonthlyStaffReportDialog
					onClose={closeDialog}
					canSelectStaff={has(REPORT_PERMISSIONS.intervention)}
					title={t('report.opForm18aReport')}
					useExportMutation={useExportOpForm18aReportMutation}
				/>
			)}
			{selected === 'spsForm' && <SpsFormReportDialog onClose={closeDialog} />}
		</>
	);
}
